import { isLoadedItem } from '@mimir/common/search/interfaces';
import { mdfState } from '@mimir/web/globals/mdfState';
import type { TimedMetadataItemModel } from '@mimir/web/model/timedMdf';
import { isActiveRestrictedMaterialItem } from '@mimir/web/util/usageRestrictionsCheck';
import type { ItemMetadataUpdater, TryUpdateMetadataResult, UpdateMetadataParams } from '@mimir/client-common/metadata/itemMetadataUpdater';
import cloneDeep from 'lodash/cloneDeep';
import { assert } from '@mimir/common/util/invariant';
import type {
  FieldValueDiff,
  MetadataSnapshot,
} from '@mimir/common/dto/mdfModels';
import keyBy from 'lodash/keyBy';
import { showMetadataConflictDialog } from '@mimir/web/dialogs/metadataConflict';
import { showSimpleAsyncDialog } from '@mimir/web/dialogs/simpleAsync';
import { isMdfTypeVisibleToUser } from '@mimir/web/store/storeMisc';
import { TOAST, showToast } from '@mimir/web/eventBus';
import { discardFileTypeFields } from '@mimir/web/components/mdf/mdf-client';
import { abortable, isAbortError } from '@mimir/common/util/abortControl';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import type { LoadedItem, LoadedSearchItem } from '@mimir/common/dto/searchResultHit';
import type { FieldValue, ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { FolderDto } from '@mimir/common/dto/folderModels';
import { FOLDER_DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { ItemType } from '@mimir/common/dto/itemTypes';

export type ItemMetadataClientHookContext = 'metadata_update' | 'metadata_merge' | 'folder_metadata_update';

export function hasRestrictedMaterial(item: LoadedItem | LoadedSearchItem, timedMdfCollectionItems: TimedMetadataItemModel[]): boolean {
  if (!isLoadedItem(item)) {
    return !!item.hasRestrictedMaterial;
  }
  return !!timedMdfCollectionItems.find(item => isActiveRestrictedMaterialItem(item, undefined));
}

export interface MetadataEditContext {
  id: string;
  metadata: ItemMetaData;
  metadataSnapshot: MetadataSnapshot;
  hasRestrictedMaterial?: boolean;
}

export interface FolderMetadataItem extends MetadataEditContext {
  metadataVersion: number;
  itemType: 'folder';
  owner: string;
  updatableBy?: ItemAccessControl;
  createdOn: string;
  name: string;
}

export function updateParamsForCurrentState(item: MetadataEditContext): UpdateMetadataParams {
  const metadataSnapshot = item.metadataSnapshot;
  assert(metadataSnapshot, 'Expecting item to update to have metadataSnapshot');
  // We want to capture both the base state and the current state that we want to save,
  // and so do a full clone of both to avoid these objects getting mutated by
  // other code.
  return cloneDeep({
    editBase: {
      metadata: metadataSnapshot.latestSnapshot,
      metadataVersion: metadataSnapshot.metadataVersion,
    },
    editedMetadata: item.metadata,
  });
}

interface UpdateMetadataFuncParams {
  item: MetadataEditContext;
  updateParams: UpdateMetadataParams;
  metadataUpdater: ItemMetadataUpdater;
  onAbortController: (abortController: AbortController) => void;
  onShowSaveCancelBtn: (showSaveSaveCancelBtn: boolean) => void;
}

export interface MetadataSaveBaseParams extends UpdateMetadataFuncParams {
  formId: string | null;
  defaultMdf: MetaDataSchema | undefined;
  emitSaveSuccess: boolean;
  onSaving: (isSaving: boolean) => void;
  onFormId: (formId: string) => void;
  onMetadataSnapshot: (metadataSnapshot: MetadataSnapshot) => void;
  onSaveSucceeded: () => void;
  onUserMadeMetadataChanges: (userMadeMetadataChanges: boolean) => void;
}

export interface ResolveConflictsAndSaveParams extends MetadataSaveBaseParams {
  conflicts: FieldValueDiff[];
}

export type StartDoSaveParams = MetadataSaveBaseParams;

export type DoSaveParams = MetadataSaveBaseParams;

export async function resolveConflictsAndSave(
  params: ResolveConflictsAndSaveParams,
): Promise<void> {
  const { formId, item, defaultMdf, updateParams, conflicts, onSaving } = params;

  const schemaFields = mdfState.state.getMdfById(formId)?.fields ?? [];
  const commonFields = defaultMdf?.fields ?? [];
  const fieldsById = keyBy([...schemaFields, ...commonFields], 'id');

  showMetadataConflictDialog(
    conflicts,
    fieldsById,
    async () => await startDoSave(params),
    async () => {
      const metadataToSave = updateParams.editedMetadata;
      for (const field of conflicts) {
        metadataToSave.formData[field.fieldId] = field.oldValue as FieldValue;

        // TODO: This is needed now to have the conflict resolution
        // reflected back to the item metadata. Should probably instead
        // look at doing that after a successful update.
        item.metadata.formData[field.fieldId] = field.oldValue as FieldValue;
      }
      await startDoSave(params);
    },
    () => { onSaving(false) },
  );
}

export async function startDoSave(
  params: StartDoSaveParams,
): Promise<void> {
  const { formId, defaultMdf, updateParams, onSaving } = params;

  const defaultId = defaultMdf?.id;
  if (formId !== updateParams.editedMetadata.formId && formId !== defaultId) {
    // itemExistingMdf is the metadata form of the item's currently "saved" metadata
    const itemExistingMdf = mdfState.state.getMdfById(formId);
    const isItemExistingMdfTypeVisibleToUser = itemExistingMdf ? isMdfTypeVisibleToUser(itemExistingMdf) : true;
    // Todo: This dialog should probably be run before conflict checking? Would reduce chance of outdated conflict check.
    void showSimpleAsyncDialog({
      closeOnActionClick: true,
      maxWidth: 320,
      icon: 'live_help',
      header: 'Change metadata type',
      subHeader: 'Save new metadata type?',
      actionText: 'Confirm',
      baseText: `Are you sure you want to change the metadata form for this item? All custom form data from the previous form will be lost, this cannot be undone!${!isItemExistingMdfTypeVisibleToUser ? ' You will not be able to select the previous type.' : ''}`,
    },
      () => doSave(params),
      undefined,
      undefined,
      () => onSaving(false)
    );
  } else {
    void doSave(params);
  }
}

export async function doSave(
  params: DoSaveParams,
) {
  const {
    item, updateParams, metadataUpdater,
    onAbortController, onShowSaveCancelBtn, onSaving, emitSaveSuccess,
    onFormId, onMetadataSnapshot, onSaveSucceeded, onUserMadeMetadataChanges
  } = params;

  try {
    onSaving(true);
    discardFileTypeFields(updateParams.editedMetadata);
    const result = await updateMetadata({ item, updateParams, metadataUpdater, onAbortController, onShowSaveCancelBtn });
    if (result.status === 'schema_conflict') {
      // TODO: Need to do give the user a choice in how to handle this.
      // But the existing websocket based auto updating does unconditional
      // updates on schema changes, completely overwriting the user's changes.
      showToast('Conflicting form type update detected', TOAST.WARNING);
      return;
    }
    if (result.status === 'fields_conflict') {
      // Let the user resolve conflicts. We want to apply the
      // same change, but based on the conflicting metadata.
      const reapplyParams: UpdateMetadataParams = {
        editBase: result.conflictMetadata,
        editedMetadata: updateParams.editedMetadata,
      };

      const payload: ResolveConflictsAndSaveParams = {
        ...params,
        conflicts: result.conflictingFields,
        updateParams: reapplyParams,
      };
      void resolveConflictsAndSave(payload);
      return;
    }

    const updatedState = result.latest;
    onUserMadeMetadataChanges(false);
    onFormId(updatedState.metadata.formId);
    const metadataSnapshot: MetadataSnapshot = {
      latestSnapshot:  updatedState.metadata,
      metadataVersion: updatedState.metadataVersion,
      conflictedFields: []
    };
    onMetadataSnapshot(metadataSnapshot);
    showToast('Metadata updated.', TOAST.SUCCESS);

    if (emitSaveSuccess) {
      onSaveSucceeded();
    }
  } catch (err) {
    if (!isAbortError(err)) {
      logErrorAndShowToast('Failed to save metadata, please try again later.', err, 'ex in ItemMetadata.doSave');
    }
  } finally {
    onSaving(false);
  }
}

export async function updateMetadata(
  params: UpdateMetadataFuncParams,
): Promise<TryUpdateMetadataResult> {
  const { item, updateParams, metadataUpdater, onAbortController, onShowSaveCancelBtn } = params;

  onShowSaveCancelBtn(false);
  const abortController = new AbortController();
  onAbortController(abortController);

  const timeout = setTimeout(() => {
    onShowSaveCancelBtn(true);
  }, 1000);

  try {
    return await abortable(
      async () => {
        return await metadataUpdater.tryUpdateMetadata(item.id, updateParams);
      },
      // TODO: pass abort controller to metadataUpdater, to avoid multiple requests after aborting.
      abortController.signal
    );
  } finally {
    clearTimeout(timeout);
    onShowSaveCancelBtn(false);
  }
}

export function constructFolderMetadataItem(folderDto: FolderDto): FolderMetadataItem {
  const metadata: ItemMetaData = folderDto.metadata ?? { formId: FOLDER_DEFAULT_MDF_ID, formData: {} };
  return {
    id: folderDto.id,
    owner: folderDto.owner,
    name: folderDto.name,
    createdOn: folderDto.createdOn,
    itemType: folderDto.itemType,
    updatableBy: folderDto.updatableBy,
    metadata,
    metadataVersion: 1,
    metadataSnapshot: {
      latestSnapshot: metadata,
      metadataVersion: 1,
      conflictedFields: [],
    },
  };
}

export function isFolderMetadataItem(params: unknown): params is FolderMetadataItem {
  if (typeof params !== 'object' || params === null) {
    return false;
  }
  if (!('itemType' in params) || (params as Record<string, unknown>).itemType !== ItemType.FOLDER) {
    return false;
  }
  const metadataSnapshot = (params as Record<string, unknown>).metadataSnapshot;
  if (!('metadataSnapshot' in params) || metadataSnapshot === null || typeof metadataSnapshot !== 'object') {
    return false;
  }
  const metadata = (params as Record<string, unknown>).metadata;
  return typeof metadata === 'object' && metadata !== null && (metadata as Record<string, unknown>).formId === FOLDER_DEFAULT_MDF_ID;
}

